import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { History, RefreshCw, Filter, ChevronRight, Trash2, X } from "lucide-react";
import { badgeFor, fmtBytes, fmtDate, buildListParams, ulogIntegrityBadge } from "@/lib/flightSessions";
import { UlogIntegrityAlerts } from "@/components/UlogIntegrityAlerts";

const keyOf = (s) => `${s.drone_id}\u0000${s.session_id}`;

function Badge({ value, testid }) {
  const b = badgeFor(value);
  return (
    <span data-testid={testid}
      className="inline-block font-mono text-[10px] px-1.5 py-0.5 rounded-sm border"
      style={{ color: b.c, borderColor: b.c + "55", background: b.c + "11" }}>
      {b.l}
    </span>
  );
}

function UlogBadge({ s }) {
  const b = ulogIntegrityBadge(s.ulog_verification_level, s.ulog_alert);
  return (
    <span data-testid={`fs-ulog-${s.session_id}`}
      className="inline-block font-mono text-[10px] px-1.5 py-0.5 rounded-sm border"
      style={{ color: b.c, borderColor: b.c + "55", background: b.c + "11" }}>
      {b.l}
    </span>
  );
}

// P.6 — confirmation destructive : le bouton reste désactivé tant que l'utilisateur
// n'a pas tapé exactement SUPPRIMER (pour le bulk) ; single = confirmation claire.
function DeleteConfirm({ open, targets, busy, onConfirm, onCancel }) {
  const [text, setText] = useState("");
  useEffect(() => { if (open) setText(""); }, [open]);
  if (!open) return null;
  const n = targets.length;
  const bulk = n > 1;
  const canConfirm = !busy && (bulk ? text.trim() === "SUPPRIMER" : true);
  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={busy ? undefined : onCancel}>
      <div data-testid="fs-delete-modal" className="w-full max-w-md mx-4 bg-[#141414] border border-[#EF4444]/30 rounded-sm p-5"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-chivo text-lg font-bold tracking-tight uppercase text-[#EF4444]">
            Supprimer {n} session{n > 1 ? "s" : ""}
          </h3>
          {!busy && <button data-testid="fs-delete-close" onClick={onCancel} className="text-[#525252] hover:text-white"><X size={16} /></button>}
        </div>
        <p className="font-mono text-xs text-[#A3A3A3] leading-relaxed">
          Seront définitivement supprimés : <span className="text-white">ULog, logs Agent, bundles, analyses dérivées, métadonnées Backend</span>.
          Cette opération est <span className="text-[#EF4444]">irréversible</span>.
        </p>
        <div className="mt-3 max-h-28 overflow-y-auto border border-white/10 rounded-sm bg-[#0A0A0A] p-2 font-mono text-[10px] text-[#737373]">
          {targets.map((t) => (<div key={keyOf(t)}>{t.drone_id} · {t.session_id}</div>))}
        </div>
        {bulk && (
          <div className="mt-4">
            <label className="font-mono text-[10px] uppercase tracking-wider text-[#525252]">
              Tapez <span className="text-[#EF4444]">SUPPRIMER</span> pour confirmer
            </label>
            <input data-testid="fs-delete-confirm-input" autoFocus value={text} disabled={busy}
              onChange={(e) => setText(e.target.value)}
              className="mt-1 w-full bg-[#0A0A0A] border border-white/15 rounded-sm px-2 py-1.5 font-mono text-xs focus:border-[#EF4444] outline-none" />
          </div>
        )}
        <div className="flex gap-2 mt-5 justify-end">
          <button data-testid="fs-delete-cancel" onClick={onCancel} disabled={busy}
            className="font-mono text-xs uppercase tracking-wider border border-white/15 hover:bg-white/10 rounded-sm px-4 py-2 transition-colors disabled:opacity-40">
            Annuler
          </button>
          <button data-testid="fs-delete-confirm" onClick={onConfirm} disabled={!canConfirm}
            className="font-mono text-xs uppercase tracking-wider rounded-sm px-4 py-2 transition-colors text-white bg-[#EF4444] hover:bg-[#DC2626] disabled:opacity-40 disabled:cursor-not-allowed">
            {busy ? "Suppression…" : "Supprimer définitivement"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function FlightSessions() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ drone_id: "", upload_status: "", analysis_status: "" });
  const [selected, setSelected] = useState(() => new Set());
  const [confirm, setConfirm] = useState({ open: false, targets: [] });
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildListParams(filters);
      const { data } = await api.get("/flight-sessions", { params });
      setSessions(data.sessions || []);
      setSelected(new Set());
    } catch (e) {
      toast.error(apiErr(e, "Impossible de charger les sessions"));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const allSelected = sessions.length > 0 && selected.size === sessions.length;
  const toggleOne = (s) => setSelected((prev) => {
    const next = new Set(prev);
    const k = keyOf(s);
    next.has(k) ? next.delete(k) : next.add(k);
    return next;
  });
  const togglePage = () => setSelected(allSelected ? new Set() : new Set(sessions.map(keyOf)));
  const clearSelection = () => setSelected(new Set());

  const selectedTargets = useMemo(
    () => sessions.filter((s) => selected.has(keyOf(s))).map((s) => ({ drone_id: s.drone_id, session_id: s.session_id })),
    [sessions, selected]);

  const runDelete = async () => {
    const targets = confirm.targets;
    setDeleting(true);
    try {
      if (targets.length === 1) {
        const t = targets[0];
        await api.delete(`/flight-sessions/${encodeURIComponent(t.drone_id)}/${encodeURIComponent(t.session_id)}`);
        toast.success("Session supprimée");
      } else {
        const { data } = await api.post("/flight-sessions/bulk-delete", { sessions: targets });
        if (data.failed > 0) toast.warning(`${data.deleted} supprimée(s), ${data.failed} échec(s)`);
        else toast.success(`${data.deleted} session(s) supprimée(s)`);
      }
      setConfirm({ open: false, targets: [] });
      await load();
    } catch (e) {
      toast.error(apiErr(e, "Échec de la suppression"));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto bg-[#0A0A0A] text-white" data-testid="flight-sessions-page">
      <div className="max-w-6xl mx-auto px-6 py-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <History size={20} className="text-[#3B82F6]" />
            <div>
              <h1 className="font-chivo font-black text-xl tracking-tight uppercase">Sessions de vol</h1>
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#525252]">
                Archive post-vol · bundles · analyse ULog
              </p>
            </div>
          </div>
          <button data-testid="fs-refresh" onClick={load}
            className="flex items-center gap-1.5 font-mono text-[11px] uppercase border border-white/15 rounded-sm px-3 py-1.5 hover:bg-white/10 transition-colors">
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Rafraîchir
          </button>
        </div>

        <UlogIntegrityAlerts refreshKey={sessions} />

        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="fs-filters">
          <Filter size={13} className="text-[#525252]" />
          <input data-testid="fs-filter-drone" placeholder="Drone ID"
            value={filters.drone_id}
            onChange={(e) => setFilters((f) => ({ ...f, drone_id: e.target.value }))}
            className="bg-[#111] border border-white/10 rounded-sm px-2 py-1 font-mono text-[11px] w-32 focus:border-[#3B82F6] outline-none" />
          <select data-testid="fs-filter-upload" value={filters.upload_status}
            onChange={(e) => setFilters((f) => ({ ...f, upload_status: e.target.value }))}
            className="bg-[#111] border border-white/10 rounded-sm px-2 py-1 font-mono text-[11px] focus:border-[#3B82F6] outline-none">
            <option value="">Upload: tous</option>
            <option value="UPLOADED">UPLOADED</option>
            <option value="UPLOADING">UPLOADING</option>
          </select>
          <select data-testid="fs-filter-analysis" value={filters.analysis_status}
            onChange={(e) => setFilters((f) => ({ ...f, analysis_status: e.target.value }))}
            className="bg-[#111] border border-white/10 rounded-sm px-2 py-1 font-mono text-[11px] focus:border-[#3B82F6] outline-none">
            <option value="">Analyse: tous</option>
            <option value="READY">READY</option>
            <option value="PENDING">PENDING</option>
            <option value="PROCESSING">PROCESSING</option>
            <option value="FAILED">FAILED</option>
          </select>
        </div>

        {/* P.5 — barre d'action bulk (admin) */}
        {isAdmin && (
          <div className="flex items-center gap-3 mb-3 h-8" data-testid="fs-bulk-bar">
            <span className="font-mono text-[11px] text-[#A3A3A3]" data-testid="fs-selected-count">
              {selected.size} sélectionnée{selected.size > 1 ? "s" : ""}
            </span>
            {selected.size > 0 && (
              <>
                <button data-testid="fs-clear-selection" onClick={clearSelection}
                  className="font-mono text-[10px] uppercase text-[#737373] hover:text-white">
                  Tout désélectionner
                </button>
                <button data-testid="fs-bulk-delete"
                  onClick={() => setConfirm({ open: true, targets: selectedTargets })}
                  className="flex items-center gap-1.5 font-mono text-[11px] uppercase border border-[#EF4444]/40 text-[#EF4444] rounded-sm px-3 py-1 hover:bg-[#EF4444]/10 transition-colors">
                  <Trash2 size={12} /> Supprimer la sélection
                </button>
              </>
            )}
          </div>
        )}

        <div className="border border-white/10 rounded-sm overflow-hidden">
          <table className="w-full text-left" data-testid="fs-table">
            <thead>
              <tr className="bg-[#111] font-mono text-[9px] tracking-[0.15em] uppercase text-[#525252]">
                {isAdmin && (
                  <th className="px-3 py-2 w-8">
                    <input type="checkbox" data-testid="fs-select-page" checked={allSelected}
                      onChange={togglePage} aria-label="Sélectionner la page courante"
                      className="accent-[#EF4444] cursor-pointer" />
                  </th>
                )}
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Drone</th>
                <th className="px-3 py-2">Session</th>
                <th className="px-3 py-2">Durée</th>
                <th className="px-3 py-2">Taille</th>
                <th className="px-3 py-2">Bundle</th>
                <th className="px-3 py-2">Upload</th>
                <th className="px-3 py-2">Intégrité</th>
                <th className="px-3 py-2">ULog</th>
                <th className="px-3 py-2">Analyse</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={isAdmin ? 12 : 11} className="px-3 py-8 text-center font-mono text-[11px] text-[#525252]">Chargement…</td></tr>
              )}
              {!loading && sessions.length === 0 && (
                <tr><td colSpan={isAdmin ? 12 : 11} className="px-3 py-8 text-center font-mono text-[11px] text-[#525252]" data-testid="fs-empty">
                  Aucune session de vol
                </td></tr>
              )}
              {!loading && sessions.map((s) => {
                const sel = selected.has(keyOf(s));
                return (
                  <tr key={keyOf(s)}
                    data-testid={`fs-row-${s.session_id}`}
                    onClick={() => navigate(`/flight-sessions/${encodeURIComponent(s.drone_id)}/${encodeURIComponent(s.session_id)}`)}
                    className={`border-t border-white/5 hover:bg-white/[0.03] cursor-pointer transition-colors ${sel ? "bg-[#EF4444]/[0.06]" : ""}`}>
                    {isAdmin && (
                      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" data-testid={`fs-select-${s.session_id}`} checked={sel}
                          onChange={() => toggleOne(s)} className="accent-[#EF4444] cursor-pointer" />
                      </td>
                    )}
                    <td className="px-3 py-2 font-mono text-[11px] text-[#A3A3A3]">{fmtDate(s.created_at)}</td>
                    <td className="px-3 py-2 font-mono text-[11px]">{s.drone_id}</td>
                    <td className="px-3 py-2 font-mono text-[11px] text-[#A3A3A3] max-w-[180px] truncate">{s.session_id}</td>
                    <td className="px-3 py-2 font-mono text-[11px] text-[#A3A3A3]" data-testid={`fs-duration-${s.session_id}`}>{s.duration_s != null ? `${s.duration_s}s` : "—"}</td>
                    <td className="px-3 py-2 font-mono text-[11px] text-[#A3A3A3]">{fmtBytes(s.total_bytes)}</td>
                    <td className="px-3 py-2"><Badge value={s.bundle_status} testid={`fs-bundle-${s.session_id}`} /></td>
                    <td className="px-3 py-2"><Badge value={s.upload_status} testid={`fs-upload-${s.session_id}`} /></td>
                    <td className="px-3 py-2"><Badge value={s.integrity_status} testid={`fs-integrity-${s.session_id}`} /></td>
                    <td className="px-3 py-2"><UlogBadge s={s} /></td>
                    <td className="px-3 py-2"><Badge value={s.analysis_status} testid={`fs-analysis-${s.session_id}`} /></td>
                    <td className="px-3 py-2 text-[#525252]">
                      <div className="flex items-center gap-2 justify-end">
                        {isAdmin && (
                          <button data-testid={`fs-delete-${s.session_id}`}
                            onClick={(e) => { e.stopPropagation(); setConfirm({ open: true, targets: [{ drone_id: s.drone_id, session_id: s.session_id }] }); }}
                            className="text-[#525252] hover:text-[#EF4444] transition-colors" title="Supprimer">
                            <Trash2 size={13} />
                          </button>
                        )}
                        <ChevronRight size={14} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <DeleteConfirm open={confirm.open} targets={confirm.targets} busy={deleting}
        onConfirm={runDelete} onCancel={() => setConfirm({ open: false, targets: [] })} />
    </div>
  );
}
